import styles from "./index.module.scss";
import { NextPage } from "next";
import StockCorporateActions from "@/components/stock-corporate-actions/StockCorporateActions";
import { Stock } from "@/models/stock";
import Head from "next/head";
import { buildStockOgImage } from "@/lib/stock-og";
import { useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import { stockTradingItemIdState } from "@/lib/store";
import Tabs, { TabItem } from "@/components/tabs/Tabs";
import { useRouter } from "next/router";
import { Box } from "@mui/material";
import CorporateActionsStockSplits from "@/components/stock-splits/CorporateActionsStockSplits";
import { stockPageGetServerSideProps } from "@/lib/stock-page-ssr";
import { stockCanonicalUrl, stockPath } from "@/lib/stock-url";

const pageTabs: TabItem[] = [
    { title: "All", value: "all" },
    { title: "Stock Splits", value: "stock-splits" },
];

const StockCorporateActionsPage: NextPage<{ stock: Stock }> = ({ stock }) => {
    const setStockTradingItemId = useSetAtom(stockTradingItemIdState);

    const router = useRouter();
    const lastPathParam = router.asPath.split("/").filter(Boolean).pop();
    const [activePageTab, setActivePageTab] = useState<string>(lastPathParam || "all");

    useEffect(() => {
        stock ? setStockTradingItemId(stock.tradingitem_id) : setStockTradingItemId(null);
    }, [stock]);

    const handleChange = (newAlignment: string, setActivePageTab: React.Dispatch<React.SetStateAction<string>>) => {
        if (newAlignment) {
            const newUrl = stockPath(stock, `corporate-actions/${newAlignment}`);

            window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, "", newUrl);

            setActivePageTab(newAlignment);
        }
    };
    return (
        <>
            {stock &&
                (() => {
                    const { ogImageUrl, cardTitle } = buildStockOgImage(stock, "Corporate Actions");
                    return (
                        <Head>
                            <title>{`${stock.name}, ${stock.exchangesymbol}:${stock.ticker} | Corporate Actions | ORTEX`}</title>
                            <link rel="canonical" href={stockCanonicalUrl(stock, "corporate-actions/all")} />
                            <meta name="keywords" content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker}, Corporate Actions`} />
                            <meta name="description" content={`Corporate Actions for ${stock.name}, ${stock.exchangesymbol}:${stock.ticker}`} />
                            <meta name="twitter:card" content="summary_large_image" />
                            <meta name="twitter:title" content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker} | Corporate Actions | ORTEX`} />
                            <meta name="twitter:description" content={`Corporate Actions for ${stock.name}, ${stock.exchangesymbol}:${stock.ticker}`} />
                            <meta name="twitter:url" content={stockCanonicalUrl(stock, "corporate-actions/all")} />
                            <meta name="twitter:image" content={ogImageUrl} />
                            <meta name="twitter:image:alt" content={cardTitle} />
                            <meta property="og:title" content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker} | Corporate Actions | ORTEX`} />
                            <meta property="og:description" content={`Corporate Actions for ${stock.name}, ${stock.exchangesymbol}:${stock.ticker}`} />
                            <meta property="og:url" content={stockCanonicalUrl(stock, "corporate-actions/all")} />
                            <meta property="og:image" content={ogImageUrl} key="og:image" />
                            <meta property="og:image:width" content="1200" />
                            <meta property="og:image:height" content="630" />
                            <meta property="og:image:alt" content={cardTitle} />
                        </Head>
                    );
                })()}
            <div className={styles.StockCorporateActionsPage}>
                {stock ? (
                    <>
                        <Tabs
                            activeTab={activePageTab}
                            alignment="center"
                            handleChange={(e, newAlignment) => handleChange(newAlignment, setActivePageTab)}
                            module={"corporate-actions"}
                            route={"s/[exchange]/[ticker]/corporate-actions"}
                            setState={setActivePageTab}
                            tabs={pageTabs}
                            setDefault={false}
                        />
                        <Box className={styles.container}>
                            {activePageTab === "all" && <StockCorporateActions stock={stock} />}
                            {activePageTab === "stock-splits" && <CorporateActionsStockSplits stock={stock} />}
                        </Box>
                    </>
                ) : (
                    <h1>Stock not found.</h1>
                )}
            </div>
        </>
    );
};

export const getServerSideProps = stockPageGetServerSideProps({ tab: "corporate-actions/all", allowedTypes: [1, 3] });

export default StockCorporateActionsPage;
